import React, { useId, useState } from 'react'
import MultipleSelect from '#app/components/multiple-select.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { SliderRange } from '#app/components/ui/slider.tsx'
import { cn } from '#app/lib/utils'
import { useDebounce } from '#app/utils/misc.tsx'

interface changeProps {
	[key: string]: string | string[]
}

interface searchProps {
	min: number
	max: number
	searchTerm: string
	tags: string[]
}

interface PowerSearchProps {
	tagOptions: { label: string; value: string }[]
	currentSearch: searchProps
	onChange: (search: changeProps) => void
	className?: string
}

const PowerSearch: React.FC<PowerSearchProps> = ({
	currentSearch,
	tagOptions,
	onChange,
	className,
}) => {
	const id = useId()
	const [{ min, max }, setLength] = useState({
		min: currentSearch?.min || 1000,
		max: currentSearch?.max || 120000,
	})
	const [searchTerm, setSearchTerm] = useState(currentSearch.searchTerm)
	const [tags, setTags] = useState(currentSearch.tags)

	const handleSearchTermChange = useDebounce((search: changeProps) => {
		onChange(search)
	}, 400)

	const [previousSearch, setPreviousSearch] = useState(currentSearch)
	if (currentSearch !== previousSearch) {
		setPreviousSearch(currentSearch)
		setLength({
			min: currentSearch.min || 1000,
			max: currentSearch.max || 120000,
		})
		setSearchTerm(currentSearch.searchTerm)
		setTags(currentSearch.tags)
	}

	return (
		<div
			className={cn(
				'flex max-xl:flex-col max-xl:space-y-2 xl:flex-row xl:items-center xl:space-x-2',
				className,
			)}
		>
			<div className="flex-1 basis-1/3">
				<Label htmlFor={id} className="sr-only">
					Search
				</Label>
				<Input
					type="search"
					name="searchTerm"
					id={id}
					defaultValue={searchTerm}
					placeholder="Search by trail name or description"
					className="w-full"
					autoFocus
					onChange={({ currentTarget: { value: searchTerm } }) => {
						setSearchTerm(searchTerm)
						const search: { [key: string]: string | string[] } = {
							searchTerm,
							tags,
						}
						if (min > 1000) {
							search.min = min.toString()
						}
						if (max < 120000) {
							search.max = max.toString()
						}
						handleSearchTermChange(search)
					}}
				/>
			</div>
			<div className="basis-1/3">
				<MultipleSelect
					options={tagOptions}
					defaultValue={currentSearch.tags}
					modalPopover={true}
					onValueChange={(tags) => {
						setTags(tags)
						const search: { [key: string]: string | string[] } = {
							searchTerm,
							tags,
						}
						if (min > 1000) {
							search.min = min.toString()
						}
						if (max < 120000) {
							search.max = max.toString()
						}
						onChange(search)
					}}
					placeholder="Select Tags"
				/>
			</div>

			<div className="basis-1/3">
				<div className="flex flex-row items-center space-x-2">
					<Label>Length:</Label>
					<span>{min / 1000}&nbsp;km</span>
					<div className="grow">
						<SliderRange
							value={[min, max]}
							min={1000}
							max={120000}
							step={1000}
							onValueChange={([min = 1000, max = 120000]) => {
								setLength({ min, max })
							}}
							onValueCommit={([min = 1000, max = 120000]) => {
								const search: { [key: string]: string | string[] } = {
									searchTerm,
									tags,
								}
								if (min > 1000) {
									search.min = min.toString()
								}
								if (max < 120000) {
									search.max = max.toString()
								}
								onChange(search)
							}}
						/>
					</div>
					<span>{max / 1000}&nbsp;km</span>
				</div>
			</div>
		</div>
	)
}

export default PowerSearch
